import { useAuthStore } from '@/core/stores/useAuthStore';
import { PROTOCOL_ERROR_CODE } from '@/core/types/error-codes';
import type { ApiErrorBody } from '@/generated/ts-rs/ApiErrorBody';
import { refreshSession } from './refresh';

export const API_BASE = import.meta.env.VITE_API_BASE;

export class ApiError extends Error {
  status: number;
  code: number;
  /**
   * 结构化错误细节。
   *
   * 合并说明:`ApiErrorBody` 的 JSON-in-string `context` 已换成结构化 `details`
   * (订单编辑单聚合根治 §1.3 的同波 breaking cutover,禁保留 alias)。console
   * 的这份客户端此前漏在切换之外 —— 它一直读 `data?.context` 且要求是 string。
   */
  details: ApiErrorBody['details'];

  constructor(status: number, message: string, code: number, details: ApiErrorBody['details'] = null) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type BodyAny = Record<string, unknown> | null;

// Read once, try JSON, keep raw text. Axum's Path extractor rejections come back
// as text/plain (e.g. "Invalid URL: ..."), so we must fall back to the raw body.
async function readBody(res: Response): Promise<{ data: BodyAny; text: string }> {
  const text = await res.text();
  if (!text) return { data: null, text: '' };
  try {
    const parsed = JSON.parse(text);
    return {
      data: typeof parsed === 'object' && parsed !== null ? (parsed as BodyAny) : null,
      text,
    };
  } catch {
    return { data: null, text };
  }
}

function parseError(data: BodyAny, text: string, statusText: string): { msg: string; code: number } {
  const jsonMsg = (data?.message ?? data?.error) as unknown;
  const msg = (typeof jsonMsg === 'string' && jsonMsg) || text.trim() || statusText;
  const code =
    typeof data?.code === 'number' ? (data.code as number)
    : typeof data?.error_code === 'number' ? (data.error_code as number)
    : undefined;
  if (code !== undefined) return { msg, code };

  const bodyKind =
    data !== null ? 'JSON error response'
    : text.trim() ? 'Non-JSON error response'
    : 'Empty error response';
  return {
    msg: `${bodyKind} missing numeric error code: ${msg || 'unknown error'}`,
    code: PROTOCOL_ERROR_CODE,
  };
}

function apiErrorFromBody(
  status: number,
  statusText: string,
  data: BodyAny,
  text: string,
): ApiError {
  const { msg, code } = parseError(data, text, statusText);
  // 只接受「扁平的 string|number|boolean|null 值」—— 与 shared 的 wire 形状一致,
  // 任何嵌套结构一律判无效而不是照单全收。
  const raw = data?.details;
  const details =
    raw !== null && typeof raw === 'object' && !Array.isArray(raw)
      ? (Object.fromEntries(
          Object.entries(raw as Record<string, unknown>).filter(
            ([, value]) =>
              value === null
              || typeof value === 'string'
              || typeof value === 'number'
              || typeof value === 'boolean',
          ),
        ) as NonNullable<ApiErrorBody['details']>)
      : null;
  return new ApiError(status, msg, code, details);
}

export async function apiErrorFromResponse(res: Response, fallbackMessage?: string): Promise<ApiError> {
  const { data, text } = await readBody(res);
  return apiErrorFromBody(res.status, fallbackMessage || res.statusText, data, text);
}

/**
 * Cloud strict ApiResponse envelope 错误形态:
 * - `{ kind: 'err', code, message, details? }` — Rust ApiResponse::Err
 * - `{ kind: 'rejected', code, message }` — store_op StoreOpResult::Rejected
 *
 * 立宪: 即使 HTTP status = 200,只要 body 是 envelope 错误形态就 throw,不允许
 * 静默把 err body 当 T 解构。raw shape (无 kind 字段) 维持 data as T。
 */
function isEnvelopeError(data: BodyAny): boolean {
  if (data === null || typeof data !== 'object') return false;
  const kind = (data as { kind?: unknown }).kind;
  return kind === 'err' || kind === 'rejected';
}

function protocolError(status: number, message: string): ApiError {
  return new ApiError(status, message, PROTOCOL_ERROR_CODE);
}

function decodeOkEnvelope<T>(data: BodyAny, status: number, label: string): T {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw protocolError(status, `${label} returned malformed ApiResponse envelope`);
  }

  const envelope = data as Record<string, unknown>;
  if (envelope.kind !== 'ok') {
    throw protocolError(status, `${label} returned non-ok ApiResponse envelope`);
  }
  if (!Object.prototype.hasOwnProperty.call(envelope, 'data')) {
    throw protocolError(status, `${label} ok envelope is missing data`);
  }

  return envelope.data as T;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  // Read body once (before 401 branch, to avoid "body already consumed")
  const { data, text } = await readBody(res);

  if (res.status === 401 && token) {
    const newToken = await refreshSession();
    if (newToken) {
      const retryHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      retryHeaders['Authorization'] = `Bearer ${newToken}`;

      const retryRes = await fetch(`${API_BASE}${path}`, {
        method,
        headers: retryHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });
      const retry = await readBody(retryRes);
      if (!retryRes.ok) {
        // Refresh succeeded but the new token is still rejected — session is
        // unrecoverable. Drop auth so ProtectedRoute redirects to /login.
        if (retryRes.status === 401) useAuthStore.getState().clearAuth();
        throw apiErrorFromBody(retryRes.status, retryRes.statusText, retry.data, retry.text);
      }
      if (isEnvelopeError(retry.data)) {
        throw apiErrorFromBody(retryRes.status, retryRes.statusText, retry.data, retry.text);
      }
      return retry.data as T;
    }
  }

  if (!res.ok) {
    throw apiErrorFromBody(res.status, res.statusText, data, text);
  }

  if (isEnvelopeError(data)) {
    throw apiErrorFromBody(res.status, res.statusText, data, text);
  }

  return data as T;
}

export async function requestFormData<T>(
  path: string,
  formData: FormData,
  token?: string,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const { data, text } = await readBody(res);

  if (res.status === 401 && token) {
    const newToken = await refreshSession();
    if (newToken) {
      const retryHeaders: Record<string, string> = { Authorization: `Bearer ${newToken}` };

      const retryRes = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: retryHeaders,
        body: formData,
      });
      const retry = await readBody(retryRes);
      if (!retryRes.ok) {
        if (retryRes.status === 401) useAuthStore.getState().clearAuth();
        throw apiErrorFromBody(retryRes.status, retryRes.statusText, retry.data, retry.text);
      }
      if (isEnvelopeError(retry.data)) {
        throw apiErrorFromBody(retryRes.status, retryRes.statusText, retry.data, retry.text);
      }
      return retry.data as T;
    }
  }

  if (!res.ok) {
    throw apiErrorFromBody(res.status, res.statusText, data, text);
  }

  if (isEnvelopeError(data)) {
    throw apiErrorFromBody(res.status, res.statusText, data, text);
  }

  return data as T;
}

export async function requestFormDataApiOk<T>(
  path: string,
  formData: FormData,
  token?: string,
): Promise<T> {
  const data = await requestFormData<unknown>(path, formData, token);
  return decodeOkEnvelope<T>(data as BodyAny, 200, path);
}

export async function requestApiOk<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
): Promise<T> {
  const data = await request<unknown>(method, path, body, token);
  return decodeOkEnvelope<T>(data as BodyAny, 200, path);
}
